import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import startCase from 'lodash.startcase';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { uuid } from 'uuidv4';

import PortfolioSection from '../Section/PortfolioSection';
import RecentTransactionCard from './RecentTransactionsCard';
import TransactionDetails from '../../Modals/Transaction/TransactionDetails';
import {
  emptyPortfolioTransactionSingle,
  portfolioTransactionsSingleFetch,
  portfolioFinancialPendingTransactions,
} from '../../../store/actions/portfolio/portfolioActions';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { formatDate } from '../../../util/helpers/dateHelpers';
import {
  transactionFieldFormatter,
  getInsiderSavingsSubtext,
} from '../../../util/helpers/transcation/transactionHelper';
import BlockSectionLoader from '../../Loader/BlockSectionLoader';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import SectionLoader from '../../Loader/SectionLoader';
import { EmptyTable } from '../../EmptyTable/EmptyTable';
import { uFlexColumn } from '../../../assets/styles/utility';
import { variables } from '../../../assets/styles/variables';
import PendingTransactionCard from './PendingTransactionCard';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { productLocationTypes } from '../../../util/enum/api/productTypes';
import {
  transactionRequiestStatusTypesArray,
  triggerOrderStatusTypesArray,
  TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER,
} from '../../../util/enum/api/transactionTypes';
import CancelPendingTransactionModal from '../../Modals/CancelPendingTransaction/CancelPendingTransactionModal';
import CancelTriggerOrderModal from '../../Modals/CancelTriggerOrder/CancelTriggerOrderModal';
import TriggerOrderDetailsModal from '../../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';

const RecentTransaction = styled.div`
  ${uFlexColumn};
  box-shadow: ${variables.shadow.boxShadow};
  border-radius: ${variables.borderRadius.borderRadius};
`;

const RecentTransactions = ({
  data,
  pendingTransactions,
  error,
  accountUid,
  isLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] = useState(
    false,
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelTransactionData, setCancelTransactionData] = useState({});
  const [
    isCancelTriggerOrderModalOpen,
    setIsCancelTriggerOrderModalOpen,
  ] = useState(false);
  const [cancelTriggerOrderData, setCancelTriggerOrderData] = useState(null);
  const [isTriggerOrderDetailsOpen, setIsTriggerOrderDetailsOpen] = useState(
    false,
  );
  const [selectedTriggerOrder, setSelectedTriggerOrder] = useState(null);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );
  const { SegregatedCaption } = useSelector(selectSettings);

  const onTransactionCardClick = useCallback(
    (transactionUid) => {
      dispatch(
        portfolioTransactionsSingleFetch({
          accountUid,
          transactionUid,
        }),
      );
      setIsTransactionDetailsOpen(true);
    },
    [accountUid, dispatch],
  );

  const handleCancelModalOpen = (accountUid, paymentUid) => {
    setIsCancelModalOpen(true);
    setCancelTransactionData({
      accountUid,
      paymentUid,
    });
  };

  const refreshTransactions = useCallback(() => {
    dispatch(portfolioFinancialPendingTransactions({ accountUid }));
  }, [accountUid, dispatch]);

  const handleCancelTriggerOrderModalOpen = useCallback(
    (triggerOrderAccountUid, triggerOrderUid, amount) => {
      setCancelTriggerOrderData({
        accountUid: triggerOrderAccountUid,
        paymentUid: triggerOrderUid,
        amount,
        refreshTransactions,
      });
      setIsCancelTriggerOrderModalOpen(true);
    },
    [refreshTransactions],
  );

  const handleCancelTriggerOrderModalClose = () => {
    setIsCancelTriggerOrderModalOpen(false);
  };

  const handleTriggerOrderDetailsOpen = (transaction) => {
    setSelectedTriggerOrder(transaction);
    setIsTriggerOrderDetailsOpen(true);
  };

  const handleTriggerOrderDetailsClose = () => {
    setIsTriggerOrderDetailsOpen(false);
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
    setCancelTransactionData({});
  };

  const renderTransactions = useCallback(() => {
    const renderBody = () => {
      const transactions = [];
      if (!isEmpty(data) && !!data.length) {
        transactions.push(...data);
      }
      if (!isEmpty(pendingTransactions) && !!pendingTransactions.length) {
        transactions.push(
          ...pendingTransactions?.map((transaction) => ({
            ...transaction,
            DateTime: transaction.RequestDate,
          })),
        );
      }
      const sortedTransactions = transactions.sort((a, b) => {
        const dateA = new Date(a.DateTime);
        const dateB = new Date(b.DateTime);
        return dateB - dateA;
      });
      const renderTransactionCard = (transaction) => {
        if (
          transaction.TransactionRequestType ===
          TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER
        ) {
          const unit = t(
            transaction.Quantity > 1
              ? 'portfolio.transactions.details.pluralUnit'
              : 'portfolio.transactions.details.singleUnit',
          );

          return (
            <PendingTransactionCard
              key={`${transaction.TriggerOrderUid}-${uuid()}`}
              month={formatDate(transaction.DateTime, 'MMMM')}
              day={formatDate(transaction.DateTime, 'd')}
              amount={transaction.Amount}
              label={t('portfolio.pendingTransactions.triggerOrderLabel', {
                quantity: transaction.Quantity,
                unit,
              })}
              product={transaction.ProductCaption}
              isSegregated={transaction.IsSegregated}
              segregatedCaption={SegregatedCaption}
              vaultName={startCase(
                parseEnumType(productLocationTypes, transaction.LocationType),
              )}
              status={parseEnumType(
                triggerOrderStatusTypesArray,
                transaction.TriggerOrderStatus,
              )}
              canCancel={transaction.CanCancel}
              accountUid={accountUid}
              paymentUid={transaction.TriggerOrderUid}
              handleModalOpen={handleCancelTriggerOrderModalOpen}
              onCardClick={() => handleTriggerOrderDetailsOpen(transaction)}
            />
          );
        }

        if (transaction.DepositKey) {
          return (
            <PendingTransactionCard
              key={`${transaction.DepositKey}-${uuid()}`}
              month={formatDate(transaction.DateTime, 'MMMM')}
              day={formatDate(transaction.DateTime, 'd')}
              amount={transaction.Amount}
              status={parseEnumType(
                transactionRequiestStatusTypesArray,
                transaction.RequestStatus,
              )}
              canCancel={transaction.CanCancel}
              action={transaction.TransactionType}
              accountUid={accountUid}
              paymentUid={transaction.DepositKey}
              handleModalOpen={handleCancelModalOpen}
            />
          );
        }

        return (
          <RecentTransactionCard
            key={`${transaction.Uid}_${transaction.DateTime}`}
            month={formatDate(transaction.DateTime, 'MMMM')}
            day={formatDate(transaction.DateTime, 'd')}
            action={
              transaction.Fields[0]
                ? transactionFieldFormatter(transaction.Fields[0])
                : null
            }
            actionSubject={
              transaction.Fields[1]
                ? transactionFieldFormatter(transaction.Fields[1])
                : null
            }
            discountSubtext={
              transaction.Fields
                ? getInsiderSavingsSubtext(transaction.Fields)
                : null
            }
            isSegregated={transaction.IsSegregated}
            status={transaction.StatusType}
            onClick={() => onTransactionCardClick(transaction.Uid)}
          />
        );
      };

      return !isEmpty(sortedTransactions) && !!sortedTransactions.length
        ? sortedTransactions.map(renderTransactionCard)
        : null;
    };

    return (
      <>
        {isLoading ? (
          <BlockSectionLoader isLoading={isLoading} />
        ) : (
          <>
            {renderBody()}
            {(isEmpty(data) || !data.length) &&
              (isEmpty(pendingTransactions) || !pendingTransactions.length) && (
                <EmptyTable>
                  <p>{t('portfolio.transactions.tableEmpty')}</p>
                </EmptyTable>
              )}
          </>
        )}
      </>
    );
  }, [
    data,
    pendingTransactions,
    accountUid,
    isLoading,
    onTransactionCardClick,
    handleCancelTriggerOrderModalOpen,
    SegregatedCaption,
    t,
  ]);

  return (
    <PortfolioSection title="Recent Transactions">
      <RecentTransaction>
        {!error ? (
          <SectionLoader isLoading={singleTransactionLoading}>
            {renderTransactions()}
          </SectionLoader>
        ) : (
          <span>{error}</span>
        )}
      </RecentTransaction>
      <TransactionDetails
        isOpen={!isEmpty(singleTransaction) && isTransactionDetailsOpen}
        onClose={() => {
          dispatch(emptyPortfolioTransactionSingle());
          setIsTransactionDetailsOpen(false);
        }}
        transaction={singleTransaction}
        error={singleTransactionError}
      />
      <CancelPendingTransactionModal
        handleClose={handleCancelModalClose}
        isOpen={isCancelModalOpen}
        cancelTransactionData={cancelTransactionData}
      />
      <CancelTriggerOrderModal
        isOpen={isCancelTriggerOrderModalOpen}
        handleClose={handleCancelTriggerOrderModalClose}
        cancelTransactionData={cancelTriggerOrderData}
      />
      <TriggerOrderDetailsModal
        isOpen={isTriggerOrderDetailsOpen}
        onClose={handleTriggerOrderDetailsClose}
        transaction={selectedTriggerOrder}
      />
    </PortfolioSection>
  );
};

RecentTransactions.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  pendingTransactions: PropTypes.arrayOf(PropTypes.shape({})),
  error: PropTypes.string,
  accountUid: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default RecentTransactions;
